import { useState, useCallback, useEffect, useRef } from 'react';
import { message } from 'antd';
import warehouseLocationApi, { F105, F1051 } from '../../../services/warehouseLocationApi';

type Mode = 'browse' | 'add' | 'edit';

export function useWarehouseLocationForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F105[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(false);

  // Employee name cache for grid display (f901_11)
  const [employeeNames, setEmployeeNames] = useState<Record<string, string>>({});
  const empCacheRef = useRef<Record<string, string>>({});

  // Main form fields
  const [f105_01, setF10501] = useState('');
  const [f105_11, setF10511] = useState('');
  const [f105_12, setF10512] = useState('');
  const [f105_13, setF10513] = useState('');
  const [f105_14, setF10514] = useState('');
  const [f105_15, setF10515] = useState('');
  const [f105_16, setF10516] = useState(false);
  const [f105_17, setF10517] = useState(false);
  const [f105_19, setF10519] = useState(false);
  const [f105_20, setF10520] = useState('');
  const [f105_18, setF10518] = useState(false);
  const [f105_91, setF10591] = useState('');
  const [f105_92, setF10592] = useState<string | null>(null);
  const [f105_93, setF10593] = useState('');

  // Detail rows (sub-table F1051)
  const [details, setDetails] = useState<F1051[]>([]);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await warehouseLocationApi.list(1, 500);
      setRecords(res.data || []);
      if ((res.data || []).length > 0) {
        setCurrentIdx(0);
      }
    } catch {
      message.error('載入資料失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const syncFields = useCallback((rec: F105) => {
    setF10501(rec.f105_01 || '');
    setF10511(rec.f105_11 || '');
    setF10512(rec.f105_12 || '');
    setF10513(rec.f105_13 || '');
    setF10514(rec.f105_14 || '');
    setF10515(rec.f105_15 || '');
    setF10516(rec.f105_16 || false);
    setF10517(rec.f105_17 || false);
    setF10519(rec.f105_19 || false);
    setF10520(rec.f105_20 || '');
    setF10518(rec.f105_18 || false);
    setF10591(rec.f105_91 || '');
    setF10592(rec.f105_92);
    setF10593(rec.f105_93 || '');
    setDetails(rec.details || []);
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    if (records.length > 0 && currentIdx >= 0 && currentIdx < records.length) {
      syncFields(records[currentIdx]);
    }
  }, [records, currentIdx, syncFields]);

  // Navigation
  const navFirst = useCallback(() => setCurrentIdx(0), []);
  const navPrev = useCallback(() => setCurrentIdx((i) => Math.max(0, i - 1)), []);
  const navNext = useCallback(() => setCurrentIdx((i) => Math.min(records.length - 1, i + 1)), [records.length]);
  const navLast = useCallback(() => setCurrentIdx(records.length - 1), [records.length]);

  const startAdd = useCallback(() => {
    setMode('add');
    setF10501('');
    setF10511('');
    setF10512('');
    setF10513('');
    setF10514('');
    setF10515('');
    setF10516(false);
    setF10517(false);
    setF10519(false);
    setF10520('');
    setF10518(false);
    setF10591('');
    setF10592(null);
    setF10593('');
    setDetails([]);
  }, []);

  const startEdit = useCallback(() => {
    setMode('edit');
  }, []);

  const cancel = useCallback(() => {
    setMode('browse');
    if (records.length > 0) {
      syncFields(records[currentIdx]);
    }
  }, [records, currentIdx, syncFields]);

  // Detail management (BR-C03)
  const addDetail = useCallback(() => {
    if (details.length >= 99) {
      message.warning('明細最多 99 筆');
      return;
    }
    const maxSeq = details.reduce((max, d) => Math.max(max, d.f1051_02), 0);
    setDetails([...details, { f1051_01: f105_01, f1051_02: maxSeq + 1, f1051_11: '' }]);
  }, [details, f105_01]);

  const removeDetail = useCallback((idx: number) => {
    setDetails((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const updateDetailEmployee = useCallback((idx: number, empCode: string) => {
    setDetails((prev) => prev.map((d, i) => i === idx ? { ...d, f1051_11: empCode } : d));
  }, []);

  // Lookup employee name from F901 (for grid column f901_11)
  const lookupEmployeeName = useCallback(async (code: string) => {
    if (!code || empCacheRef.current[code]) return;
    try {
      const result = await warehouseLocationApi.lookupEmployee(code);
      empCacheRef.current[code] = result.name;
      setEmployeeNames((prev) => ({ ...prev, [code]: result.name }));
    } catch {
      // Employee not found - leave blank
    }
  }, []);

  // Lookup employee names when details change
  useEffect(() => {
    details.forEach((d) => {
      if (d.f1051_11 && !empCacheRef.current[d.f1051_11]) {
        lookupEmployeeName(d.f1051_11);
      }
    });
  }, [details, lookupEmployeeName]);

  const buildPayload = useCallback((): Partial<F105> => ({
    f105_01: f105_01.trim(),
    f105_11: f105_11,
    f105_12: f105_12,
    f105_13: f105_13,
    f105_14: f105_14,
    f105_15: f105_15,
    f105_16: f105_16,
    f105_17: f105_17,
    f105_20: f105_20,
    f105_18: f105_18,
    f105_91: f105_91,
    f105_93: f105_93,
    details: details,
  }), [f105_01, f105_11, f105_12, f105_13, f105_14, f105_15, f105_16, f105_17, f105_20, f105_18, f105_91, f105_93, details]);

  const save = useCallback(async () => {
    setLoading(true);
    try {
      const payload = buildPayload();
      if (mode === 'add') {
        await warehouseLocationApi.create(payload);
        message.success('新增成功');
      } else {
        await warehouseLocationApi.update(f105_01, payload);
        message.success('儲存成功');
      }
      setMode('browse');
      await loadRecords();
    } catch (err: any) {
      if (err?.response?.status === 422) {
        const errors = err.response.data?.errors;
        if (Array.isArray(errors) && errors.length > 0) {
          message.error(errors[0].message);
        }
      } else {
        message.error('儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [mode, f105_01, buildPayload, loadRecords]);

  const doDelete = useCallback(async () => {
    if (!f105_01) return;
    setLoading(true);
    try {
      await warehouseLocationApi.delete(f105_01);
      message.success('刪除成功');
      await loadRecords();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [f105_01, loadRecords]);

  const quickSearch = useCallback((keyword: string) => {
    const idx = records.findIndex((r) => r.f105_01.toUpperCase().startsWith(keyword.toUpperCase()));
    if (idx >= 0) {
      setCurrentIdx(idx);
    } else {
      message.warning('查無資料');
    }
  }, [records]);

  return {
    mode, loading, records, currentIdx,
    f105_01, f105_11, f105_12, f105_13, f105_14, f105_15,
    f105_16, f105_17, f105_18, f105_19, f105_20,
    f105_91, f105_92, f105_93,
    setF10501, setF10511, setF10512, setF10513, setF10514, setF10515,
    setF10516, setF10517, setF10518, setF10520,
    details, addDetail, removeDetail, updateDetailEmployee,
    employeeNames, lookupEmployeeName,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete,
    quickSearch,
    recordCount: records.length,
    recordPosition: records.length > 0 ? currentIdx + 1 : 0,
  };
}
